import React from "react";
import { CircleCheck } from "lucide-react";
import { MapContainer, TileLayer, Marker } from "react-leaflet";
import L from "leaflet";
import type { Coords } from "@/shared/utils/location";
import {
  GeofencingGpsNotice,
  type GeofencingGpsNoticeVariant,
} from "./GeofencingGpsBanner";

import "leaflet/dist/leaflet.css";

// Default marker icon (Leaflet's default paths break with Vite, so we set URLs explicitly)
const markerIcon = L.icon({
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

export interface LocationCheckCardProps {
  /** Current position; when null and !loading, shows unverified/error state */
  coords: Coords | null;
  /** Optional address string (e.g. from reverse geocoding) */
  address?: string | null;
  /** Whether location is still being fetched */
  loading?: boolean;
  /**
   * When false, only the header row is shown (no map, accuracy line, or loading/unavailable body).
   */
  showMap?: boolean;
  /**
   * When set, shows a dismissible geofencing GPS notice inside this card (below the Location Check header).
   */
  geofencingNoticeVariant?: GeofencingGpsNoticeVariant;
}

const DEFAULT_ZOOM = 16;

export function LocationCheckCard({
  coords,
  address: _address,
  loading,
  showMap = true,
  geofencingNoticeVariant,
}: LocationCheckCardProps) {
  const geofencingNotice =
    geofencingNoticeVariant != null ? (
      <div className="mt-3">
        <GeofencingGpsNotice variant={geofencingNoticeVariant} />
      </div>
    ) : null;
  if (!showMap) {
    if (loading) {
      return (
        <div
          id="location-card"
          className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100 mx-4 mb-4"
        >
          <div className="flex justify-between items-center">
            <label className="text-sm font-semibold text-gray-700">
              Location Check
            </label>
            <div className="flex items-center gap-1 text-xs font-medium text-amber-600 bg-amber-50 px-2 py-1 rounded-md">
              Getting location…
            </div>
          </div>
          {geofencingNotice}
        </div>
      );
    }
    if (!coords) {
      return (
        <div
          id="location-card"
          className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100 mx-4 mb-4"
        >
          <div className="flex justify-between items-center">
            <label className="text-sm font-semibold text-gray-700">
              Location Check
            </label>
            <div className="flex items-center gap-1 text-xs font-medium text-amber-600 bg-amber-50 px-2 py-1 rounded-md">
              Unavailable
            </div>
          </div>
          {geofencingNotice}
        </div>
      );
    }
    return (
      <div
        id="location-card"
        className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100 mx-4 mb-4"
      >
        <div className="flex justify-between items-center">
          <label className="text-sm font-semibold text-gray-700">
            Location Check
          </label>
          <div className="flex items-center gap-1 text-xs font-medium text-emerald-600 bg-emerald-50 px-2 py-1 rounded-md">
            <CircleCheck className="h-3.5 w-3.5" aria-hidden />
            Verified
          </div>
        </div>
        {geofencingNotice}
      </div>
    );
  }

  if (loading) {
    return (
      <div
        id="location-card"
        className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100 mx-4 mb-4"
      >
        <div className="flex justify-between items-center mb-3">
          <label className="text-sm font-semibold text-gray-700">
            Location Check
          </label>
          <div className="flex items-center gap-1 text-xs font-medium text-amber-600 bg-amber-50 px-2 py-1 rounded-md">
            Getting location…
          </div>
        </div>
        {geofencingNotice}
        <div className="h-32 w-full rounded-xl overflow-hidden border border-gray-100 bg-gray-100 flex items-center justify-center">
          <span className="text-sm text-gray-500">Requesting GPS…</span>
        </div>
      </div>
    );
  }

  if (!coords) {
    return (
      <div
        id="location-card"
        className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100 mx-4 mb-4"
      >
        <div className="flex justify-between items-center mb-3">
          <label className="text-sm font-semibold text-gray-700">
            Location Check
          </label>
          <div className="flex items-center gap-1 text-xs font-medium text-amber-600 bg-amber-50 px-2 py-1 rounded-md">
            Unavailable
          </div>
        </div>
        {geofencingNotice}
        <div className="h-32 w-full rounded-xl overflow-hidden border border-gray-100 bg-gray-50 flex items-center justify-center">
          <span className="text-sm text-gray-500">
            Location was not available. You can still start your shift.
          </span>
        </div>
      </div>
    );
  }

  const position: [number, number] = [coords.latitude, coords.longitude];
  const accuracyText =
    coords.accuracy != null
      ? `GPS accuracy: ±${Math.round(coords.accuracy)} meters`
      : "GPS accuracy: unknown";

  return (
    <div
      id="location-card"
      className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100 mx-4 mb-4"
    >
      <div className="flex justify-between items-center mb-3">
        <label className="text-sm font-semibold text-gray-700">
          Location Check
        </label>
        <div className="flex items-center gap-1 text-xs font-medium text-emerald-600 bg-emerald-50 px-2 py-1 rounded-md">
          <CircleCheck className="h-3.5 w-3.5" aria-hidden />
          Verified
        </div>
      </div>
      {geofencingNotice}
      <div className="h-32 w-full rounded-xl overflow-hidden border border-gray-100 [&_.leaflet-container]:rounded-xl">
        <MapContainer
          center={position}
          zoom={DEFAULT_ZOOM}
          scrollWheelZoom={true}
          style={{ height: "100%", width: "100%" }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <Marker position={position} icon={markerIcon} draggable={false} />
        </MapContainer>
      </div>
      <p className="text-xs text-gray-400 mt-2 text-center">{accuracyText}</p>
    </div>
  );
}
